import React, { useState } from "react";
import type { CheckboxesQuestion } from "@r6digital/rapidstor-types/types/Questionnaire";
import BigHeader from "../BigHeader";
import { Col, Collapse, Row } from "react-bootstrap";
import SmallDetail from "../SmallDetail";
import classes from "./Question.module.scss";
import store from "../../store";
import {
  addResponseArray,
  deleteResponse,
  deleteResponseArray,
  selectResponses,
} from "../../store/questionnaireSlice";
import { useSelector } from "react-redux";
import CheckboxesResponse from "./CheckboxesResponse";
import Header from "../Header";
import { ReactComponent as IconPlus } from "../../assets/icon-plus.svg";
import { ReactComponent as IconMinus } from "../../assets/icon-minus.svg";
import MediumHeader from "../MediumHeader";

interface Props {
  id: string;
  question: CheckboxesQuestion;
}

function CheckboxesQuestion({ id, question }: Props) {
  const collapsible = question.appearance === "collapsible";
  const questionnaireResponses = useSelector(selectResponses);
  const [collapsed, setCollapsed] = useState<boolean>(collapsible);

  const onSelect = (answer: string | boolean | number, checked: boolean) => {
    if (checked) {
      store.dispatch(addResponseArray({ id, response: answer }));
    } else {
      store.dispatch(deleteResponseArray({ id, response: answer }));
    }
  };

  const responses = question.responses.map((response, index) => {
    let checked = false;
    const responseArray = questionnaireResponses?.[id] || [];
    if (Array.isArray(responseArray)) {
      checked = responseArray.includes(response.answer);
    }

    return (
      <CheckboxesResponse
        key={index}
        question={question}
        response={response}
        onChange={onSelect}
        checked={checked}
      />
    );
  });

  const onClickClear = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    store.dispatch(deleteResponse(id));
  };

  const collapseIcon = collapsed ? (
    <IconPlus className={`me-2 ${classes.collapseIcon}`} />
  ) : (
    <IconMinus className={`me-2 ${classes.collapseIcon}`} />
  );

  const headerClicked = () => {
    if (collapsible) {
      setCollapsed(!collapsed);
    }
  };

  return (
    <div className={classes.checkboxes}>
      {collapsible ? (
        <MediumHeader
          className={`${classes.title} ${classes.collapsible}`}
          onClick={headerClicked}
        >
          {collapseIcon} {question.title}
        </MediumHeader>
      ) : (
        <BigHeader className={classes.title}>{question.title}</BigHeader>
      )}
      <Collapse in={!collapsed}>
        <div>
          {question.subheader && (
            <Header level={4} bold className={classes.subheader}>
              {question.subheader}
            </Header>
          )}
          <Row>
            <Col>{responses}</Col>
          </Row>
          <a
            href="#"
            className={`link transparent ${classes.clearSelection}`}
            onClick={onClickClear}
          >
            Clear selection
          </a>

          {question.extraText && (
            <SmallDetail style={{ opacity: 0.7 }}>{question.extraText}</SmallDetail>
          )}
        </div>
      </Collapse>
    </div>
  );
}

export default CheckboxesQuestion;
